import { useAuth } from "@clerk/react";
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ApiError } from "@/features/api/apiClient";
import {
  listAdminPurchaseEvents,
  type AdminPurchaseEventListResponse,
  type AdminPurchaseEventSummary,
} from "@/features/admin/adminApi";

const PAGE_SIZE = 20;

const STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "received", label: "Received" },
  { value: "processed", label: "Processed" },
  { value: "duplicate", label: "Duplicate" },
  { value: "invalid_signature", label: "Invalid signature" },
  { value: "unknown_product", label: "Unknown product" },
  { value: "failed", label: "Failed" },
  { value: "refunded", label: "Refunded" },
  { value: "ignored", label: "Ignored" },
];

const PROVIDER_OPTIONS = [
  { value: "", label: "All providers" },
  { value: "stripe", label: "Stripe" },
  { value: "jvzoo", label: "JVZoo" },
  { value: "warriorplus", label: "WarriorPlus" },
  { value: "admin_manual", label: "Admin Manual" },
  { value: "manual", label: "Manual" },
];

function formatDate(value: string | null): string {
  if (!value) return "—";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}

function statusBadgeClass(status: string): string {
  switch (status) {
    case "processed":
      return "bg-emerald-100 text-emerald-800";
    case "failed":
    case "invalid_signature":
      return "bg-red-100 text-red-800";
    case "duplicate":
      return "bg-amber-100 text-amber-800";
    case "unknown_product":
      return "bg-orange-100 text-orange-800";
    case "received":
      return "bg-blue-100 text-blue-800";
    case "refunded":
      return "bg-purple-100 text-purple-800";
    case "ignored":
      return "bg-slate-100 text-slate-600";
    default:
      return "bg-slate-100 text-slate-600";
  }
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-semibold ${statusBadgeClass(status)}`}
    >
      {status.replace(/_/g, " ")}
    </span>
  );
}

function EventCard({ event }: { event: AdminPurchaseEventSummary }) {
  const identifier = event.transactionId ?? event.eventId ?? event.id;

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800">
              {event.sourceLabel}
            </span>
            <StatusBadge status={event.status} />
            {event.ghlSent ? (
              <span className="rounded bg-teal-100 px-2 py-0.5 text-xs font-semibold text-teal-800">
                GHL sent
              </span>
            ) : null}
          </div>
          <p className="mt-2 text-sm font-semibold text-slate-800">
            {event.eventType}
          </p>
          {event.email ? (
            <p className="mt-1 break-all text-sm text-slate-600">{event.email}</p>
          ) : null}
          <p className="mt-1 break-all font-mono text-xs text-slate-400">
            {identifier}
          </p>
          {event.errorMessage ? (
            <p className="mt-2 text-xs text-red-700">{event.errorMessage}</p>
          ) : null}
        </div>
        <Link
          to={`/admin/purchase-events/${encodeURIComponent(event.id)}`}
          className="inline-flex w-full items-center justify-center rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700 sm:w-fit sm:flex-shrink-0"
        >
          View
        </Link>
      </div>
      <div className="mt-3 grid gap-2 text-xs sm:grid-cols-3">
        <div>
          <p className="text-slate-500">Action taken</p>
          <p className="mt-0.5 font-semibold text-slate-800">
            {event.tierGranted ?? "—"}
          </p>
        </div>
        <div>
          <p className="text-slate-500">Product</p>
          <p className="mt-0.5 break-all font-semibold text-slate-800">
            {event.productId ?? "—"}
          </p>
        </div>
        <div>
          <p className="text-slate-500">Received</p>
          <p className="mt-0.5 font-semibold text-slate-800">
            {formatDate(event.createdAt)}
          </p>
        </div>
      </div>
    </article>
  );
}

function AccessDenied() {
  return (
    <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
      <p className="text-sm font-semibold text-slate-500">Not found</p>
      <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
        This page is not available
      </h2>
      <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
        Purchase event logs are only available to authorised operators.
      </p>
    </section>
  );
}

export function AdminPurchaseEvents() {
  const { getToken, isLoaded } = useAuth();
  const [query, setQuery] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [provider, setProvider] = useState("");
  const [status, setStatus] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState<AdminPurchaseEventListResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDenied, setIsDenied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const filters = useMemo(
    () => ({ q: submittedQuery, provider, status, page, pageSize: PAGE_SIZE }),
    [page, submittedQuery, provider, status],
  );

  useEffect(() => {
    async function loadEvents() {
      if (!isLoaded) return;

      setIsLoading(true);
      setIsDenied(false);
      setError(null);

      try {
        const token = await getToken();
        setData(await listAdminPurchaseEvents(token, filters));
      } catch (err) {
        if (
          err instanceof ApiError &&
          (err.status === 401 || err.status === 403 || err.status === 404)
        ) {
          setIsDenied(true);
          return;
        }
        setError(
          err instanceof Error ? err.message : "Unable to load purchase events.",
        );
      } finally {
        setIsLoading(false);
      }
    }

    void loadEvents();
  }, [filters, getToken, isLoaded]);

  if (isDenied) {
    return <AccessDenied />;
  }

  return (
    <div className="space-y-6">
      <Link
        to="/admin"
        className="inline-flex text-sm font-semibold text-indigo-700 hover:text-indigo-900"
      >
        Back to Admin
      </Link>

      <section className="rounded-lg border border-slate-200 bg-white p-6 shadow-sm">
        <p className="text-sm font-semibold text-indigo-700">Admin</p>
        <h2 className="mt-2 text-2xl font-semibold tracking-normal text-slate-950">
          Purchase Events
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600">
          Read-only log of payment, webhook and manual access events.
        </p>
      </section>

      <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-5">
        <form
          className="grid gap-3 lg:grid-cols-[1fr_180px_180px_auto]"
          onSubmit={(event) => {
            event.preventDefault();
            setPage(1);
            setSubmittedQuery(query.trim());
          }}
        >
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by email, transaction ID, event ID…"
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          />
          <select
            value={provider}
            onChange={(e) => {
              setPage(1);
              setProvider(e.target.value);
            }}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {PROVIDER_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <select
            value={status}
            onChange={(e) => {
              setPage(1);
              setStatus(e.target.value);
            }}
            className="rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-950 outline-none transition focus:border-indigo-500 focus:ring-2 focus:ring-indigo-100"
          >
            {STATUS_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <button
            type="submit"
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-indigo-700"
          >
            Search
          </button>
        </form>
      </section>

      {error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">
          {error}
        </div>
      ) : null}

      {isLoading ? (
        <div className="h-72 animate-pulse rounded-lg bg-white" />
      ) : (
        <section className="grid gap-3">
          {(data?.events ?? []).map((event) => (
            <EventCard key={event.id} event={event} />
          ))}

          {data?.events.length === 0 ? (
            <div className="rounded-lg border border-dashed border-slate-300 bg-white p-8 text-center">
              <p className="font-semibold text-slate-950">No events found</p>
              <p className="mt-2 text-sm text-slate-600">
                Try a different search or filter.
              </p>
            </div>
          ) : null}
        </section>
      )}

      <div className="flex items-center justify-between gap-3">
        <button
          type="button"
          disabled={page <= 1 || isLoading}
          onClick={() => setPage((p) => Math.max(1, p - 1))}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Previous
        </button>
        <p className="text-sm text-slate-600">Page {page}</p>
        <button
          type="button"
          disabled={!data?.pagination.hasNextPage || isLoading}
          onClick={() => setPage((p) => p + 1)}
          className="rounded-md border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50"
        >
          Next
        </button>
      </div>
    </div>
  );
}
